import React, { useCallback } from 'react'
import type { FC, SyntheticEvent } from 'react'
import { Card, Button, IconCheck, IconWarningCircle, csx } from '@vtex/admin-ui'
import type { StyleProp } from '@vtex/admin-ui'
import { FormattedMessage } from 'react-intl'
import { useIsConnected } from '@wora/detect-network'
import { useRelayEnvironment } from 'relay-hooks'
import type EnvironmentIDB from 'react-relay-offline/lib/runtime/EnvironmentIDB'

import { getAppInfo } from '../../core/utils/appInfo'
import { useIsRelayOfflinePendingActions } from '../../core/components/RelayEnvironmentProvider/hooks/useIsRelayOfflinePendingActions'
import { TopbarLeftArea } from '../Instore/components/Header/Topbar'
import { clearAllCache } from './utils/clearAllCache'
import { logger } from '../../logger'
import { IconOnline } from './icons/IconOnline'
import { IconOffline } from './icons/IconOffline'
import { sizeToRem } from '../../core/theme/theme'
import { TextWithEllipsis } from '../../ui/components/TextWithEllipsis'

const lineBaseStyle: StyleProp = {
  lineHeight: 'body',
  fontSize: sizeToRem(2),
}

const iconBaseStyle: StyleProp = {
  minWidth: '1.25rem',
  minHeight: '1.25rem',
  width: '1.25rem',
  height: '1.25rem',
}

const AppInfoLine: FC = ({ children }) => {
  return (
    <div
      className={csx({
        ...lineBaseStyle,
        marginTop: '$space-1',
      })}
    >
      {children}
    </div>
  )
}

type AppInfoIconLineProps = { intlId: string }

const AppInfoIconLine: FC<AppInfoIconLineProps> = ({ intlId, children }) => {
  return (
    <AppInfoLine>
      <div
        className={csx({
          fontSize: '1rem',
          display: 'flex',
        })}
      >
        {children}
        <div
          className={csx({
            ...lineBaseStyle,
            marginTop: '$space-02',
            marginLeft: '$space-1',
          })}
        >
          <FormattedMessage id={intlId} />
        </div>
      </div>
    </AppInfoLine>
  )
}

type AppInfoProps = {
  intlId: string
  firstLine?: boolean
  wrapChildrenWithLineStyle?: boolean
}

const AppInfo: FC<AppInfoProps> = ({
  intlId,
  children,
  wrapChildrenWithLineStyle = true,
  firstLine = false,
}) => {
  return (
    <div className={!firstLine ? csx({ marginTop: '$space-6' }) : undefined}>
      <div
        className={csx({
          ...lineBaseStyle,
          color: '$grayTransparent50',
          fontSize: '1rem',
        })}
      >
        <FormattedMessage id={intlId} />
      </div>
      {wrapChildrenWithLineStyle ? (
        <AppInfoLine css={{ fontSize: '1rem' }}>{children}</AppInfoLine>
      ) : (
        children
      )}
    </div>
  )
}

const UpdateAppButton: FC = () => {
  const environment = useRelayEnvironment() as ReturnType<
    typeof EnvironmentIDB.create
  >

  const handleUpdateApp = useCallback(
    async (event: SyntheticEvent) => {
      event.preventDefault()

      try {
        await clearAllCache()

        await environment.clearCache()
      } catch (error: unknown) {
        logger.error({
          workflowType: 'clear-cache-error-thrown',
          error: error instanceof Error ? error : new Error(String(error)),
        })
      }

      window.location.reload()
    },
    [environment]
  )

  return (
    <div
      className={csx({
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
      })}
    >
      <Button
        onClick={handleUpdateApp}
        size="large"
        className={csx({
          marginTop: '$space-6',
          width: 'full',
          '@desktop': {
            width: 'auto',
          },
        })}
      >
        <FormattedMessage id="statusScreenUpdateApp" />
      </Button>
    </div>
  )
}

const AppStatusContent: FC = () => {
  const isConnected = useIsConnected()

  const appInfo = getAppInfo()

  const webVersion = typeof window !== 'undefined' ? window.APP_VERSION : ''

  const hasPendingInformation = useIsRelayOfflinePendingActions()

  return (
    <Card
      className={csx({
        flexDirection: 'column',
        backgroundColor: '$white',
        padding: '$space-6',
      })}
    >
      <AppInfo intlId="statusScreenAppVersionLabel" firstLine>
        {appInfo.version ? `${appInfo.version} | ${webVersion}` : webVersion}
      </AppInfo>

      <AppInfo intlId="statusScreenDeviceIdLabel">{appInfo.deviceId}</AppInfo>

      <AppInfo
        intlId="statusScreenConnectionLabel"
        wrapChildrenWithLineStyle={false}
      >
        <>
          {isConnected ? (
            <AppInfoIconLine intlId="statusScreenOnline">
              <IconOnline />
            </AppInfoIconLine>
          ) : (
            <AppInfoIconLine intlId="statusScreenOffline">
              <IconOffline />
            </AppInfoIconLine>
          )}
          {hasPendingInformation ? (
            <AppInfoIconLine intlId="statusScreenHasPendingInformation">
              <IconWarningCircle className={csx({ ...iconBaseStyle })} />
            </AppInfoIconLine>
          ) : (
            <AppInfoIconLine intlId="statusScreenUpdatedInformation">
              <IconCheck className={csx({ ...iconBaseStyle })} />
            </AppInfoIconLine>
          )}
        </>
      </AppInfo>
    </Card>
  )
}

export const AppStatusScreen: FC = () => {
  return (
    <>
      <TopbarLeftArea>
        <TextWithEllipsis variant="title2">
          <FormattedMessage id="menu-appStatus" />
        </TextWithEllipsis>
      </TopbarLeftArea>
      <div
        className={csx({
          height: '100%',
          padding: '$space-6',
          background: '$blue05',
          alignItems: 'center',
          justifyContent: 'center',
        })}
      >
        <div>
          <AppStatusContent />
        </div>
        <UpdateAppButton />
      </div>
    </>
  )
}
